import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Refresh,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { cashService, CashPosition, CashSummary, CashMetrics } from '../services/cashService';
import { fundsApi, Fund } from '../services/fundsApi';
import { LoggerService } from '../services/LoggerService';
import CashProjectionChart from '../components/cash/CashProjectionChart';

const CashPositionsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [positions, setPositions] = useState<CashPosition[]>([]);
  const [summary, setSummary] = useState<CashSummary | null>(null);
  const [metrics, setMetrics] = useState<CashMetrics | null>(null);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [funds, setFunds] = useState<Fund[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadFunds();
  }, []);

  useEffect(() => {
    if (selectedFund) {
      loadCashData();
    }
  }, [selectedFund, dateFrom, dateTo]);

  const loadFunds = async () => {
    try {
      setLoading(true);
      const fundsResponse = await fundsApi.getFunds({}, 1, 100);
      const fundsData = fundsResponse.data || fundsResponse.items || [];
      setFunds(fundsData);
      if (fundsData.length > 0 && !selectedFund) {
        setSelectedFund(fundsData[0].id);
      }
    } catch (err: any) {
      LoggerService.error('Error loading funds:', err);
      setError('Failed to load funds');
    } finally {
      // Ensure we don't get stuck with an infinite spinner if funds fail to load
      setLoading(false);
    }
  };

  const loadCashData = async () => {
    if (!selectedFund) return;

    try {
      setLoading(true);
      setError(null);

      const [positionsData, summaryData, metricsData] = await Promise.all([
        cashService.getFundCashPositions(selectedFund, {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }),
        cashService.getCashSummary(selectedFund),
        cashService.getCashMetrics(selectedFund, 30),
      ]);

      setPositions(positionsData.positions);
      setSummary(summaryData);
      setMetrics(metricsData);
    } catch (err: any) {
      LoggerService.error('Error loading cash data:', err);
      setError(err.response?.data?.detail || 'Failed to load cash positions');
      enqueueSnackbar('Failed to load cash positions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && !positions.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Cash Positions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Fund</InputLabel>
                <Select
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  label="Fund"
                >
                  {funds.map((fund) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadCashData}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Current Balance
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(summary.current_balance, summary.currency)}
                </Typography>
                {summary.last_update && (
                  <Typography variant="caption" color="textSecondary">
                    Updated: {formatDate(summary.last_update)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          {metrics && (
            <>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Net Cash Flow (30d)
                    </Typography>
                    <Typography
                      variant="h4"
                      color={metrics.net_cash_flow >= 0 ? 'success.main' : 'error.main'}
                    >
                      {metrics.net_cash_flow >= 0 ? (
                        <TrendingUp />
                      ) : (
                        <TrendingDown />
                      )}{' '}
                      {formatCurrency(metrics.net_cash_flow, summary.currency)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Average Balance (30d)
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(metrics.average_balance, summary.currency)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Inflows (30d)
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(metrics.total_inflows, summary.currency)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* Cash Projections Chart */}
      {selectedFund && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <CashProjectionChart fundId={selectedFund} currency={summary?.currency} />
          </CardContent>
        </Card>
      )}

      {/* Positions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cash Position History
          </Typography>
          {positions.length === 0 ? (
            <Alert severity="info">No cash positions found for the selected period.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Opening Balance</TableCell>
                    <TableCell align="right">Inflows</TableCell>
                    <TableCell align="right">Outflows</TableCell>
                    <TableCell align="right">Net Flow</TableCell>
                    <TableCell align="right">Closing Balance</TableCell>
                    <TableCell>Account Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((position) => (
                    <TableRow key={position.id}>
                      <TableCell>{formatDate(position.date)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(position.opening_balance, position.currency)}
                      </TableCell>
                      <TableCell align="right" style={{ color: 'green' }}>
                        {formatCurrency(position.inflows, position.currency)}
                      </TableCell>
                      <TableCell align="right" style={{ color: 'red' }}>
                        {formatCurrency(position.outflows, position.currency)}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          color: position.net_cash_flow >= 0 ? 'green' : 'red',
                        }}
                      >
                        {formatCurrency(position.net_cash_flow, position.currency)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(position.closing_balance, position.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip label={position.account_type} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashPositionsPage;

